import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import React from "react";
import { useTranslation } from "react-i18next";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { SettingsV2PageTitle } from "web-apps/company/components/companySettingsV2/companySettingsV2.styled";
import {
  SystemSettingsLayout,
  SystemSettingsCanvas,
  SystemSettingsTabsCard,
  SystemSettingsTabsRow,
  SystemSettingsStatusCard,
  SystemSettingsStatusDescription,
  SystemSettingsStatusTitle,
} from "./companySystemSettings.styled";
import { CompanySystemSettingsContent } from "./companySystemSettingsContent.component";
import { useCompanySystemSettings } from "./useCompanySystemSettings.hook";
import { SystemSettingsCategory } from "./companySystemSettings.types";

export const CompanySystemSettings: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const {
    activeCategory,
    setActiveCategory,
    formData,
    setFormData,
    madeChanges,
    categoryOptions,
    moduleSettings,
    activeCategoryHasSettings,
    activeCategoryHasChanges,
    isLoading,
    isReady,
    isSaving,
    loadFailed,
    handleUpdate,
    retryLoad,
    setModuleToggle,
  } = useCompanySystemSettings();

  const handleCategoryChange = (category: SystemSettingsCategory): void => {
    setActiveCategory(category);

    const params = new URLSearchParams(location.search);
    params.set("page", "System");
    params.set("category", category);

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  return (
    <SystemSettingsLayout>
      <Prompt
        when={madeChanges}
        message={t("ProfileCompany.YouHaveUnSavedChanges")}
      />

      <SystemSettingsCanvas>
        <SystemSettingsTabsCard>
          <SettingsV2PageTitle>{t("System.System")}</SettingsV2PageTitle>
          <SystemSettingsTabsRow>
            <SegmentController
              options={categoryOptions}
              value={activeCategory}
              onChange={handleCategoryChange}
              disabled={!isReady}
              tone="dark"
            />
          </SystemSettingsTabsRow>
        </SystemSettingsTabsCard>

        {loadFailed ? (
          <SystemSettingsStatusCard>
            <SystemSettingsStatusTitle>
              {t("Alerts.OhNo")}
            </SystemSettingsStatusTitle>
            <SystemSettingsStatusDescription>
              {t("System.CompanySystemSettingsNotLoaded")}
            </SystemSettingsStatusDescription>
            <PrimaryButton label={t("General.TryAgain")} onClick={retryLoad} />
          </SystemSettingsStatusCard>
        ) : !isReady ? (
          <SystemSettingsStatusCard>
            <SystemSettingsStatusTitle>
              {t("General.Loading")}
            </SystemSettingsStatusTitle>
          </SystemSettingsStatusCard>
        ) : (
          <CompanySystemSettingsContent
            activeCategory={activeCategory}
            formData={formData}
            setFormData={setFormData}
            moduleSettings={moduleSettings}
            setModuleToggle={setModuleToggle}
            activeCategoryHasSettings={activeCategoryHasSettings}
            activeCategoryHasChanges={activeCategoryHasChanges}
            canSave={!isLoading && !isSaving}
            isSaving={isSaving}
            handleUpdate={handleUpdate}
            t={t}
          />
        )}
      </SystemSettingsCanvas>
    </SystemSettingsLayout>
  );
};
